<template>
  <RouterLink
    :to="to"
    :id="`nav-${idSuffix}`"
    class="nav-item"
    :class="{ 'nav-item--indent': indent }"
    active-class="nav-item--active"
    :exact="to === '/'"
  >
    <!-- Icon -->
    <span class="nav-icon" v-html="iconSvg"></span>
    <!-- Label -->
    <span class="nav-label">{{ label }}</span>
    <!-- Badge ─────────────────────────────────────────────────────────────
         REQ-OPS-034: eine Zahl, die nicht serverseitig gezaehlt ist, darf hier
         nicht als exakte Zahl stehen. `badgeExact = false` stellt ihr deshalb
         ein „≥" voran; den Grund nennt `badgeTitle`. Der Text kommt fertig
         uebersetzt herein — dieses Bauteil kennt kein i18n. -->
    <span
      v-if="badge && badge > 0"
      class="nav-badge"
      :class="{ 'nav-badge--approx': badgeExact === false }"
      :title="badgeTitle"
    >{{ badgeExact === false ? '≥ ' : '' }}{{ badge }}</span>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  to:       string;
  icon:     string;
  label:    string;
  idSuffix: string;
  badge?:   number | null;
  /**
   * Ist die Zahl im Abzeichen serverseitig gezaehlt (REQ-OPS-034)? Fehlt die
   * Angabe, gilt sie als exakt — Abzeichen ohne Zahlbezug (`null`) sind davon
   * ohnehin nicht betroffen.
   */
  badgeExact?: boolean;
  /** Bereits uebersetzte Erlaeuterung, falls die Zahl nur eine Untergrenze ist. */
  badgeTitle?: string;
  indent?:  boolean;
}>();

const icons: Record<string, string> = {
  dashboard: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>`,
  print:     `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8" rx="1"/></svg>`,
  printer:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>`,
  server:    `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>`,
  users:     `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  company:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  gear:      `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
  mail:      `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>`,
  code:        `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`,
  schankanlage:`<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3h8v3H8z"/><path d="M8 6h8l1 13H7L8 6z"/><path d="M16 11h3a2 2 0 0 1 0 4h-3"/><line x1="5" y1="22" x2="19" y2="22"/></svg>`,
  location:  `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
};

const iconSvg = computed(() => icons[props.icon] ?? icons.gear);
</script>

<style scoped>
.nav-item {
  display:         flex;
  align-items:     center;
  gap:             9px;
  padding:         8px 10px;
  border-radius:   var(--radius-md);
  text-decoration: none;
  color:           var(--text-secondary);
  font-size:       0.845rem;
  font-weight:     500;
  transition:      background var(--t-fast), color var(--t-fast);
  cursor:          pointer;
  user-select:     none;
  margin-bottom:   1px;
}

.nav-item:hover {
  background: var(--color-surface-2);
  color:      var(--text-primary);
}

.nav-item--active {
  background:  var(--color-primary-dim);
  color:       var(--color-primary-dark);
  font-weight: 600;
}

.nav-item--active .nav-icon { color: var(--color-primary); }

/* Eingerückt (Sub-Items) */
.nav-item--indent {
  padding-left: 18px;
  font-size:    0.82rem;
}

.nav-icon {
  display:     flex;
  align-items: center;
  flex-shrink: 0;
  color:       currentColor;
  opacity:     0.7;
}

.nav-item--active .nav-icon { opacity: 1; }

.nav-label { flex: 1; }

.nav-badge {
  background:    var(--color-primary);
  color:         #fff;
  font-size:     0.65rem;
  font-weight:   700;
  padding:       1px 6px;
  border-radius: 20px;
  min-width:     18px;
  text-align:    center;
  flex-shrink:   0;
}

/* Untergrenze statt gezaehlter Zahl (REQ-OPS-034) — sichtbar zurueckgenommen. */
.nav-badge--approx {
  background: var(--color-surface-3);
  color:      var(--text-secondary);
}
</style>
